import { useState, useCallback } from 'react'
import { FiCopy, FiHelpCircle, FiRefreshCw } from 'react-icons/fi'
import { RiCheckLine } from 'react-icons/ri'
import { GTooltip } from 'components/basic-blocks'

interface CopyablePillProps {
	label: string
	value: string
	copyValue?: string
}

const CopyablePill = ({ label, value, copyValue }: CopyablePillProps) => {
	const [copied, setCopied] = useState(false)

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(copyValue || value)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}, [value, copyValue])

	return (
		<div className="bg-[var(--lavender-fill-soft,rgba(238,237,254,0.8))] border border-t-card-border rounded-[20px] px-3 py-2 flex items-center justify-center gap-[10px] w-[305px] overflow-hidden">
			<p className="text-[12px] leading-[20px] font-medium font-inter text-t-desc-gray w-[230px]">
				<span className="font-bold">{label}</span>
				{` = ${value}`}
			</p>
			<button
				onClick={handleCopy}
				className="shrink-0 w-4 h-4 flex items-center justify-center"
			>
				{copied ? (
					<RiCheckLine className="w-4 h-4 text-[#0B9055]" />
				) : (
					<FiCopy className="w-4 h-4 text-t-desc-gray hover:text-t-email-blue" />
				)}
			</button>
		</div>
	)
}

interface DnsInstructionsProps {
	subdomain: string
	domain: string
	target: string
	onRegenerate?: () => void
}

export const DnsInstructions = ({
	subdomain,
	domain,
	target,
	onRegenerate
}: DnsInstructionsProps) => {
	return (
		<div className="flex flex-col gap-[8px]">
			<div className="flex items-center justify-between h-[29px] gap-1">
				<h3 className="md:text-[16px] text-[14px] md:leading-[18px] leading-[16px] font-medium text-t-google-ad">
					DNS Instructions
				</h3>
				{onRegenerate && (
					<button
						onClick={onRegenerate}
						className="flex items-center gap-2 text-[14px] leading-[22px] font-medium text-t-desc-gray hover:text-t-email-blue"
					>
						<FiRefreshCw className="w-5 h-5" />
						Use a different domain
					</button>
				)}
			</div>

			<div className="flex flex-col gap-3">
				<div className="flex gap-3 items-center">
					<div className="bg-t-step-inactive rounded-full w-[23.5px] h-[24px] flex items-center justify-center shrink-0">
						<span className="text-[12px] leading-[24px] font-bold text-t-email-blue">
							1
						</span>
					</div>
					<p className="text-[14px] md:leading-[32px] text-t-checkbox-unactive">
						Go to your{' '}
						<span className="font-semibold">
							domain&apos;s DNS configuration panel
						</span>
					</p>
					<GTooltip
						position="top"
						content="Your DNS configuration panel is where you manage domain records. This is usually found in your domain registrar or hosting provider's dashboard."
					>
						<FiHelpCircle className="w-4 h-4 text-t-gray-input shrink-0 cursor-pointer" />
					</GTooltip>
				</div>

				<div className="flex gap-3 items-center">
					<div className="bg-t-step-inactive rounded-full w-[23.5px] h-[24px] flex items-center justify-center shrink-0">
						<span className="text-[12px] leading-[24px] font-bold text-t-email-blue">
							2
						</span>
					</div>
					<p className="text-[14px] md:leading-[32px] text-t-checkbox-unactive">
						Create a new CNAME record with the settings below
					</p>
				</div>

				<div className="flex flex-col gap-3 items-start md:px-[33px] px-0 md:py-[5px] py-0">
					<CopyablePill label="Name" value={subdomain} />
					<CopyablePill label="Target" value={`${domain}`} copyValue={target} />
					<CopyablePill
						label="TTL"
						value="14400 (or your default TTL)"
						copyValue="14400"
					/>
				</div>

				<div className="flex gap-3 items-center">
					<div className="bg-t-step-inactive rounded-full w-[23.5px] h-[24px] flex items-center justify-center shrink-0">
						<span className="text-[12px] leading-[24px] font-bold text-t-email-blue">
							3
						</span>
					</div>
					<p className="text-[14px] md:leading-[32px] text-t-checkbox-unactive">
						<span className="font-semibold">
							If you&apos;re using Cloudflare
						</span>
						, set Proxy status to &quot;DNS only&quot;;
					</p>
				</div>

				<div className="flex gap-3 items-center">
					<div className="bg-t-step-inactive rounded-full w-[23.5px] h-[24px] flex items-center justify-center shrink-0">
						<span className="text-[12px] leading-[24px] font-bold text-t-email-blue">
							4
						</span>
					</div>
					<p className="flex-1 text-[14px] md:leading-[20px] text-t-checkbox-unactive">
						<span className="font-semibold">Save the DNS record.</span> This may
						take some time to propagate (usually 5-30 minutes);
					</p>
				</div>

				<div className="flex gap-3 items-center">
					<div className="bg-t-step-inactive rounded-full w-[23.5px] h-[24px] flex items-center justify-center shrink-0">
						<span className="text-[12px] leading-[24px] font-bold text-t-email-blue">
							5
						</span>
					</div>
					<p className="text-[14px] md:leading-[32px] text-t-checkbox-unactive">
						After the installation, click on the button to continue.
					</p>
				</div>
			</div>
		</div>
	)
}
